import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { getUsers, saveUser, deleteUser, createSystemUser, compressImage, clearAllCats } from '../services/db';
import { X, Check, Trash2, Shield, User as UserIcon, Loader2, UserPlus, Mail, Lock, KeyRound, Phone, MessageCircle, Pencil, Save, Ban, Camera, AlertTriangle } from 'lucide-react';

interface UserManagementProps {
  currentUser: User;
  onClose: () => void;
}

export const UserManagement: React.FC<UserManagementProps> = ({ currentUser, onClose }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [isClearing, setIsClearing] = useState(false);
  
  // Create User State
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState<'admin' | 'user'>('user');
  const [createError, setCreateError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit Phone State
  const [editingPhoneEmail, setEditingPhoneEmail] = useState<string | null>(null);
  const [tempPhone, setTempPhone] = useState('');

  // Avatar Upload State
  const [loadingAvatarEmail, setLoadingAvatarEmail] = useState<string | null>(null);

  const isAdmin = currentUser.role === 'admin';

  const loadUsers = async () => {
    setLoading(true);
    const data = await getUsers();
    setUsers(data);
    setLoading(false);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const toggleApproval = async (user: User) => {
    if (!isAdmin) return;
    const updatedUser = { ...user, isApproved: !user.isApproved };
    await saveUser(updatedUser);
    loadUsers();
  };

  const handleDelete = async (email: string) => {
    if (!isAdmin) return;
    if (confirm(`¿Realmente eliminar usuario ${email}?`)) {
      await deleteUser(email);
      loadUsers();
    }
  };

  const handleClearDatabase = async () => {
      if (!isAdmin) return;
      if (confirm("ACHTUNG: Möchten Sie wirklich ALLE Katzen aus der Datenbank löschen?")) {
          if (confirm("Wirklich sicher? Dies kann nicht rückgängig gemacht werden!")) {
              setIsClearing(true);
              try {
                  await clearAllCats();
                  alert("Datenbank wurde erfolgreich geleert.");
                  window.location.reload(); // Hard reload to clear all states
              } catch (e) {
                  alert("Fehler beim Löschen: " + e);
              } finally {
                  setIsClearing(false);
              }
          }
      }
  };

  const startEditPhone = (user: User) => {
      setEditingPhoneEmail(user.email);
      setTempPhone(user.phone || '');
  };

  const savePhone = async (user: User) => {
      const updatedUser = { ...user, phone: tempPhone };
      await saveUser(updatedUser);
      setEditingPhoneEmail(null);
      loadUsers();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>, targetUser: User) => {
    if (e.target.files && e.target.files[0]) {
        setLoadingAvatarEmail(targetUser.email);
        try {
            const photoUrl = await compressImage(e.target.files[0]);
            const updatedUser = { ...targetUser, photoUrl };
            await saveUser(updatedUser);
            // Optimistic update locally to feel faster
            setUsers(prev => prev.map(u => u.email === targetUser.email ? updatedUser : u));
        } catch (err) {
            console.error("Fehler beim Bild-Upload", err);
            alert("Fehler beim Hochladen des Bildes.");
        } finally {
            setLoadingAvatarEmail(null);
        }
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
      e.preventDefault();
      setCreateError('');
      
      if (newPassword.length < 6) {
          setCreateError('Passwort muss mindestens 6 Zeichen lang sein.');
          return;
      }

      setIsSubmitting(true);
      try {
          await createSystemUser(newEmail, newPassword, newName, newRole);
          // Reset Form
          setNewName('');
          setNewEmail('');
          setNewPassword('');
          setIsCreating(false);
          await loadUsers();
      } catch (err: any) {
          console.error(err);
          if (err.code === 'auth/email-already-in-use') {
              setCreateError('E-Mail wird bereits verwendet.');
          } else if (err.code === 'auth/weak-password') {
              setCreateError('Passwort ist zu schwach.');
          } else {
              setCreateError('Fehler: ' + err.message);
          }
      } finally {
          setIsSubmitting(false);
      }
  };

  const getWhatsAppLink = (phone: string) => {
      const cleaned = phone.replace(/[^0-9]/g, '');
      return `https://wa.me/${cleaned}`;
  };

  // Helper Component for Avatar
  const UserAvatar = ({ user, size = "md" }: { user: User, size?: "md" | "lg" }) => {
      const isMe = user.email === currentUser.email;
      const canEdit = isMe || isAdmin;
      const isLoading = loadingAvatarEmail === user.email;
      
      const sizeClasses = size === "md" ? "h-10 w-10" : "h-14 w-14";
      const iconSize = size === "md" ? "h-5 w-5" : "h-7 w-7";

      return (
        <div className={`relative group ${sizeClasses} flex-shrink-0`}>
            <div className={`w-full h-full rounded-full overflow-hidden flex items-center justify-center border border-gray-200 ${user.role === 'admin' ? 'bg-purple-100' : 'bg-gray-100'}`}>
                {isLoading ? (
                    <Loader2 className={`${iconSize} animate-spin text-purple-600`} />
                ) : user.photoUrl ? (
                    <img src={user.photoUrl} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                    <UserIcon className={`${iconSize} ${user.role === 'admin' ? 'text-purple-600' : 'text-gray-500'}`} />
                )}
            </div>
            
            {canEdit && !isLoading && (
                <label className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                    <Camera className="text-white h-4 w-4 drop-shadow-md" />
                    <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={(e) => handleAvatarChange(e, user)} 
                    />
                </label>
            )}
        </div>
      );
  };

  // Regular users only see approved users
  const displayedUsers = isAdmin ? users : users.filter(u => u.isApproved);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-0 md:p-4 backdrop-blur-sm">
      <div className="w-full h-full md:h-auto md:max-h-[90vh] max-w-5xl bg-white md:rounded-2xl shadow-2xl overflow-hidden animate-[fadeIn_0.3s_ease-out] flex flex-col">
        <div className="flex items-center justify-between border-b p-4 bg-gray-50 flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <Shield className={isAdmin ? "text-purple-600" : "text-gray-400"} />
            {isAdmin ? "Gestión de usuarios" : "Lista de contactos del equipo"}
          </h2>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-gray-200">
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Create User Form - Collapsible (Admin Only) */}
        {isAdmin && (
            <div className="bg-purple-50 border-b border-purple-100 flex-shrink-0">
                {!isCreating ? (
                    <button 
                        onClick={() => setIsCreating(true)}
                        className="w-full p-3 text-purple-700 font-medium flex items-center justify-center gap-2 hover:bg-purple-100 transition-colors"
                    >
                        <UserPlus className="h-5 w-5" /> Crear nuevo usuario
                    </button>
                ) : (
                    <form onSubmit={handleCreateUser} className="p-4 space-y-4">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="font-bold text-purple-800">Crear nuevo usuario</h3>
                            <button type="button" onClick={() => setIsCreating(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Name</label>
                                <input 
                                    type="text" 
                                    required 
                                    className="w-full rounded-md border-purple-200 p-2 text-sm focus:ring-purple-500 focus:border-purple-500" 
                                    placeholder="Vorname Nachname"
                                    value={newName}
                                    onChange={e => setNewName(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Rolle</label>
                                <select 
                                    className="w-full rounded-md border-purple-200 p-2 text-sm focus:ring-purple-500 focus:border-purple-500 bg-white"
                                    value={newRole}
                                    onChange={(e) => setNewRole(e.target.value as any)}
                                >
                                    <option value="user">Usuario</option>
                                    <option value="admin">Administrator</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">E-Mail</label>
                                <input 
                                    type="email" 
                                    required 
                                    className="w-full rounded-md border-purple-200 p-2 text-sm focus:ring-purple-500 focus:border-purple-500" 
                                    placeholder="email@beispiel.de"
                                    value={newEmail}
                                    onChange={e => setNewEmail(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Passwort (min. 6 Zeichen)</label>
                                <input 
                                    type="password" 
                                    required 
                                    className="w-full rounded-md border-purple-200 p-2 text-sm focus:ring-purple-500 focus:border-purple-500" 
                                    placeholder="******"
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                />
                            </div>
                        </div>

                        {createError && (
                            <div className="text-red-600 text-sm bg-red-50 p-2 rounded border border-red-100 flex items-center gap-2">
                                <KeyRound className="h-4 w-4" /> {createError}
                            </div>
                        )}

                        <div className="flex justify-end pt-2">
                            <button 
                                type="submit" 
                                disabled={isSubmitting}
                                className="bg-purple-600 text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-purple-700 shadow-sm disabled:opacity-50 flex items-center gap-2"
                            >
                                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                Benutzer anlegen
                            </button>
                        </div>
                    </form>
                )}
            </div>
        )}

        <div className="p-0 overflow-y-auto flex-1 bg-gray-50">
          {loading ? (
             <div className="flex justify-center items-center p-8">
               <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
             </div>
          ) : displayedUsers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              Keine Benutzer gefunden.
            </div>
          ) : (
            <>
                {/* --- DESKTOP TABLE VIEW (MD UP) --- */}
                <div className="hidden md:block overflow-x-auto bg-white">
                    <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Benutzer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kontakt</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rolle</th>
                        {isAdmin && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>}
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {displayedUsers.map((user) => {
                            const isMe = user.email === currentUser.email;
                            const isEditingThis = editingPhoneEmail === user.email;

                            return (
                            <tr key={user.email} className={!user.isApproved ? 'bg-gray-50' : ''}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                    <UserAvatar user={user} size="md" />
                                    <div className="ml-3">
                                    <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                                        {user.name}
                                        {isMe && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 rounded-full">ICH</span>}
                                    </div>
                                    <div className="text-sm text-gray-500">{user.email}</div>
                                    </div>
                                </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-3">
                                        {/* Phone Display / Edit */}
                                        <div className="flex flex-col">
                                            {isEditingThis ? (
                                                <div className="flex items-center gap-1">
                                                    <input 
                                                        type="text" 
                                                        value={tempPhone}
                                                        onChange={e => setTempPhone(e.target.value)}
                                                        className="w-32 text-sm border rounded p-1"
                                                        placeholder="Tel. Nr"
                                                        autoFocus
                                                    />
                                                    <button onClick={() => savePhone(user)} className="text-green-600 hover:bg-green-100 p-1 rounded"><Save className="h-4 w-4"/></button>
                                                    <button onClick={() => setEditingPhoneEmail(null)} className="text-gray-400 hover:bg-gray-100 p-1 rounded"><X className="h-4 w-4"/></button>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2 group">
                                                    <span className="text-sm text-gray-700">{user.phone || '-'}</span>
                                                    {(isMe || isAdmin) && (
                                                        <button onClick={() => startEditPhone(user)} className="text-gray-400 hover:text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <Pencil className="h-3 w-3" />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            
                                            {/* Contact Actions */}
                                            {user.phone && !isEditingThis && (
                                                <div className="flex gap-2 mt-1">
                                                    <a 
                                                        href={`tel:${user.phone}`}
                                                        className="p-1.5 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 transition-colors"
                                                        title="Anrufen"
                                                    >
                                                        <Phone className="h-3.5 w-3.5" />
                                                    </a>
                                                    <a 
                                                        href={getWhatsAppLink(user.phone)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-1.5 bg-green-100 text-green-600 rounded-full hover:bg-green-200 transition-colors"
                                                        title="WhatsApp"
                                                    >
                                                        <MessageCircle className="h-3.5 w-3.5" />
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {user.role}
                                </td>
                                {isAdmin && (
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {user.isApproved ? (
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                            Aktiv
                                            </span>
                                        ) : (
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                            Passiv/Gesperrt
                                            </span>
                                        )}
                                    </td>
                                )}
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex justify-end gap-2">
                                {isAdmin && user.email !== 'admin@tierheim.de' && (
                                    <>
                                    <button 
                                        onClick={() => toggleApproval(user)}
                                        className={`p-1.5 rounded-md ${user.isApproved ? 'bg-orange-100 text-orange-600 hover:bg-orange-200' : 'bg-green-100 text-green-600 hover:bg-green-200'}`}
                                        title={user.isApproved ? "Benutzer passiv schalten" : "Benutzer aktiv schalten"}
                                    >
                                        {user.isApproved ? <Ban className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                                    </button>
                                    <button 
                                        onClick={() => handleDelete(user.email)}
                                        className="p-1.5 rounded-md bg-red-100 text-red-600 hover:bg-red-200"
                                        title="Löschen"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                    </>
                                )}
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                    </table>
                </div>

                {/* --- MOBILE CARD VIEW (MD DOWN) --- */}
                <div className="md:hidden space-y-4 p-4">
                    {displayedUsers.map((user) => {
                        const isMe = user.email === currentUser.email;
                        const isEditingThis = editingPhoneEmail === user.email;

                        return (
                            <div key={user.email} className={`bg-white rounded-xl p-4 shadow-sm border ${!user.isApproved ? 'border-l-4 border-l-red-400' : 'border-gray-200'}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <UserAvatar user={user} size="md" />
                                        <div>
                                            <div className="font-bold text-gray-800 flex items-center gap-2">
                                                {user.name}
                                                {isMe && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 rounded-full">ICH</span>}
                                            </div>
                                            <div className="text-xs text-gray-500">{user.email}</div>
                                        </div>
                                    </div>
                                    <span className="text-xs font-semibold px-2 py-1 bg-gray-100 rounded text-gray-600 uppercase">
                                        {user.role}
                                    </span>
                                </div>

                                <div className="space-y-3">
                                    {/* Phone Section Mobile */}
                                    <div className="bg-gray-50 rounded-lg p-3">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-xs font-semibold text-gray-500 uppercase">Telefon / Kontakt</span>
                                            {(isMe || isAdmin) && !isEditingThis && (
                                                <button onClick={() => startEditPhone(user)} className="text-xs text-purple-600 font-medium flex items-center gap-1">
                                                    <Pencil className="h-3 w-3" /> Ändern
                                                </button>
                                            )}
                                        </div>

                                        {isEditingThis ? (
                                            <div className="flex items-center gap-2 mt-2">
                                                <input 
                                                    type="text" 
                                                    value={tempPhone}
                                                    onChange={e => setTempPhone(e.target.value)}
                                                    className="w-full text-sm border rounded-md p-2"
                                                    placeholder="Tel. Nr"
                                                    autoFocus
                                                />
                                                <button onClick={() => savePhone(user)} className="bg-green-100 text-green-700 p-2 rounded-md"><Save className="h-4 w-4"/></button>
                                                <button onClick={() => setEditingPhoneEmail(null)} className="bg-gray-200 text-gray-600 p-2 rounded-md"><X className="h-4 w-4"/></button>
                                            </div>
                                        ) : (
                                            <div className="flex justify-between items-center mt-1">
                                                <span className="text-sm text-gray-800 font-medium">{user.phone || '-'}</span>
                                                {user.phone && (
                                                    <div className="flex gap-3">
                                                        <a 
                                                            href={`tel:${user.phone}`}
                                                            className="p-2 bg-blue-100 text-blue-600 rounded-full"
                                                        >
                                                            <Phone className="h-4 w-4" />
                                                        </a>
                                                        <a 
                                                            href={getWhatsAppLink(user.phone)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="p-2 bg-green-100 text-green-600 rounded-full"
                                                        >
                                                            <MessageCircle className="h-4 w-4" />
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Admin Actions Mobile */}
                                    {isAdmin && (
                                        <div className="pt-3 border-t border-gray-100 grid grid-cols-2 gap-3">
                                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                                Status: 
                                                {user.isApproved ? (
                                                    <span className="text-green-600 font-bold">Aktiv</span>
                                                ) : (
                                                    <span className="text-red-600 font-bold">Passiv</span>
                                                )}
                                            </div>
                                            
                                            {user.email !== 'admin@tierheim.de' && (
                                                <div className="flex justify-end gap-2 col-span-2">
                                                    <button 
                                                        onClick={() => toggleApproval(user)}
                                                        className={`flex-1 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 ${user.isApproved ? 'bg-orange-50 text-orange-700 border border-orange-200' : 'bg-green-50 text-green-700 border border-green-200'}`}
                                                    >
                                                        {user.isApproved ? (
                                                            <> <Ban className="h-4 w-4" /> Passiv schalten </>
                                                        ) : (
                                                            <> <Check className="h-4 w-4" /> Aktivieren </>
                                                        )}
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDelete(user.email)}
                                                        className="py-2 px-4 rounded-lg bg-red-50 text-red-700 border border-red-200"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
                
                 {/* --- DANGER ZONE (ADMIN ONLY) --- */}
                 {isAdmin && (
                    <div className="mt-8 p-6 bg-red-50 border-t border-red-100">
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-red-100 rounded-full flex-shrink-0">
                                <AlertTriangle className="h-6 w-6 text-red-600" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-red-900">Zona de peligro</h3>
                                <p className="text-sm text-red-700 mt-1 mb-4 max-w-2xl">
                                    Aquí puede restablecer toda la base de datos de gatos. Todos los animales registrados, vacunas e imágenes serán eliminados irreversiblemente. Las cuentas de usuario permanecerán intactas.
                                </p>
                                <button 
                                    onClick={handleClearDatabase}
                                    disabled={isClearing}
                                    className="px-4 py-2 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 shadow-sm disabled:opacity-50 flex items-center gap-2"
                                >
                                    {isClearing ? <Loader2 className="h-4 w-4 animate-spin"/> : <Trash2 className="h-4 w-4"/>}
                                    Datenbank zurücksetzen (Alle Katzen löschen)
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};